import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useStatsigClient } from '@statsig/react-bindings'
import { layoutStore } from 'shared/layout'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { useBillingViewContext } from 'pages/settings/pages/billing/context/billingViewContext'
import { UpgradeFirstPage } from 'pages/settings/pages/billing/pages/upgrade/ui/UpgradeFirstPage/UpgradeFirstPage'
import { UpgradeSecondPage } from 'pages/settings/pages/billing/pages/upgrade/ui/UpgradeSecondPage/UpgradeSecondPage'
import { UpgradeAddOnsPage } from 'pages/settings/pages/billing/pages/upgrade/ui/UpgradeAddOnsPage/UpgradeAddOnsPage'
import { UpgradeCheckoutPage } from 'pages/settings/pages/billing/pages/upgrade/ui/UpgradeCheckoutPage/UpgradeCheckoutPage'
import { BreakpointsUpgradeEnum } from 'pages/settings/pages/billing/pages/upgrade/constants/breakpoints'
import {
  ProviderBillingUpgradePage,
  useBillingUpgradePageContext,
} from 'pages/settings/pages/billing/pages/upgrade/context/upgradeContext'
import {
  BillingUpgradePageStore,
  type IUpgradePageLocationState,
} from 'pages/settings/pages/billing/pages/upgrade/store/upgradePageStore'
import styles from './styles.module.scss'

const UpgradePageContent = observer(() => {
  const billingStore = useBillingViewContext()
  const store = useBillingUpgradePageContext()
  const { getExperiment, isLoading: isStatsigLoading } = useStatsigClient()
  const { isFirstStep, currentStep, isUpgradeModalV2_2 } = store

  useEffect(() => {
    if (isStatsigLoading) return

    const initializeStore = async () => {
      await billingStore.init()
      const isV2_2 =
        getExperiment('upgrade_modal_addons_step_ab').get('modal_version', 'V2.1') === 'V2.2'
      store.init({ isV2_2 })
    }

    initializeStore()
  }, [store, billingStore, isStatsigLoading, getExperiment])

  useEffect(() => {
    layoutStore.setBreakpoints(BreakpointsUpgradeEnum)

    return () => {
      layoutStore.setDefault()
    }
  }, [])

  const renderStep = () => {
    if (isFirstStep) return <UpgradeFirstPage />

    if (isUpgradeModalV2_2) {
      if (currentStep === 2) return <UpgradeAddOnsPage />
      if (currentStep === 3) return <UpgradeCheckoutPage />
    }

    return <UpgradeSecondPage />
  }

  return (
    <div className={isUpgradeModalV2_2 ? styles.containerV2_2 : styles.container}>
      {renderStep()}
    </div>
  )
})

export const UpgradePage = observer(() => {
  const entities = useEntitiesContext()
  const location = useLocation()
  const navigate = useNavigate()
  const locationState = location.state as IUpgradePageLocationState | null
  const [upgradeStore] = useState(() => new BillingUpgradePageStore(entities, locationState))

  useEffect(() => {
    if (locationState?.checkOpportunity) {
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [])

  return (
    <ProviderBillingUpgradePage value={upgradeStore}>
      <UpgradePageContent />
    </ProviderBillingUpgradePage>
  )
})
